import styled from "styled-components/macro";
import { TILE_WIDTH_PX_LG, getIsIe11 } from "../../../../constants/constants";
import {
  producingColor,
  textOnProducing,
  plannedColor,
  textOnPlanned,
  waitingColor,
  textOnWaiting,
  unplannedColor,
  textOnUnplanned,
  untaggedColor,
  textOnUnknown,
  noProductionScheduledColor,
  textOnNotScheduled,
  plantLossColor,
  textOnPlant,
  uncollectedColor,
  textOnUncollected,
  medGrey,
  darkModeModalBackgroundColor,
  alertColor,
} from "../../../../constants/colors";

const SECTION_HEIGHT_PCTS = [24, 59];

const responsiveWidth = (isLarge, ems, tileWidth, scaleMultiplier = 1) => {
  return isLarge
    ? `${(tileWidth / TILE_WIDTH_PX_LG) * ems}em`
    : `${ems * scaleMultiplier}em`;
};
// calculate responsive font size from num tiles & available window area
// For font sizing in large tiles mode, we need to remove the tileWidthMultiplier effect
// since it should only affect the physical width of the tile, not the font sizes
export const responsiveFontSize = (
  large,
  tileWidth,
  rems,
  scaleMultiplier = 1,
  tileWidthMultiplier = 1
) => {
  // In large tiles mode, we divide by tileWidthMultiplier to remove its effect from font sizing
  const adjustedTileWidth = large ? tileWidth / tileWidthMultiplier : tileWidth;
  return large
    ? `${(adjustedTileWidth / 100) * rems}rem`
    : `${rems * scaleMultiplier}rem`;
};

// In large-tiles mode, we need to be careful not to override the controlled dimensions
// This CSS will no longer set dimensions, as they come from the component props
const largeProcessTileCSS = () => `
    text-align: center;
    box-sizing: border-box;
    `;

export const ProcessTileStyles = styled.div.attrs<{
  $tileWidth: number;
  $tileHeight: number;
  $isLargeTiles: boolean;
  $isDesktopXlOrLarger: boolean;
  $isLongProcessName: boolean;
  $isVeryLongProcessName: boolean;
  $isDarkMode: boolean;
  $hideMetricOnTile: boolean;
  $tileScaleMultiplier: number;

  $getFontSize?: (rems: number) => string;
  $getWidth?: (ems: number) => string;
}>(props => ({
  $getFontSize: rems =>
    responsiveFontSize(
      props.$isLargeTiles || props.$isDesktopXlOrLarger,
      props.$tileWidth,
      rems,
      props.$tileScaleMultiplier,
      props.$tileWidthMultiplier
    ),
  $getWidth: ems =>
    responsiveWidth(
      props.$isLargeTiles || props.$isDesktopXlOrLarger,
      ems,
      props.$tileWidth,
      props.$tileScaleMultiplier
    ),
}))<{
  $tileWidth: number;
  $tileHeight: number;
  $isLargeTiles: boolean;
  $isDesktopXlOrLarger: boolean;
  $isLongProcessName: boolean;
  $isVeryLongProcessName: boolean;
  $isDarkMode: boolean;
  $hideMetricOnTile: boolean;
  $tileScaleMultiplier: number;

  $getFontSize?: (rems: number) => string;
  $getWidth?: (ems: number) => string;
}>`
  button {
    /* cancel out MUI Button styles */
    width: 100%;
    height: 100%;
    padding: 0 !important;
    font-family: inherit !important;
    text-transform: none !important;
    line-height: normal !important;
    border-radius: 0px !important;
  }

  overflow: hidden;
  .process-tile {
    border-radius: 2px;
    &.expanded {
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }
    position: relative;
    text-align: center;
    width: ${props => props.$tileWidth}px;
    height: ${props => props.$tileHeight}px;
    ${props => (props.$isLargeTiles ? largeProcessTileCSS() : "")}
    display: flex;
    gap: 2%;
    .process-name {
      margin-top: -1.4em;
    }
    justify-self: center;
    flex-direction: column;
    align-items: center;
    justify-items: center;
    &:hover {
      opacity: 0.8;
    }
    &.negative {
      background-color: ${untaggedColor};
    }
  }

  .process-tile-producing {
    background-color: ${producingColor};
    color: ${textOnProducing};
    fill: ${textOnProducing};
    stroke: ${textOnProducing};
  }
  .process-tile-planned {
    background-color: ${plannedColor};
    color: ${textOnPlanned};
    fill: ${textOnPlanned};
    stroke: ${textOnPlanned};
  }
  .process-tile-waiting {
    background-color: ${waitingColor};
    color: ${textOnWaiting};
    fill: ${textOnWaiting};
    stroke: ${textOnWaiting};
  }
  .process-tile-unplanned {
    background-color: ${unplannedColor};
    color: ${textOnUnplanned};
    fill: ${textOnUnplanned};
    stroke: ${textOnUnplanned};
  }
  .process-tile-untagged,
  .process-tile-unknown {
    background-color: ${untaggedColor};
    color: ${textOnUnknown};
    fill: ${textOnUnknown};
    stroke: ${textOnUnknown};
  }
  .process-tile-notScheduled {
    background-color: ${noProductionScheduledColor};
    color: ${textOnNotScheduled};
    fill: ${textOnNotScheduled};
    stroke: ${textOnNotScheduled};
  }
  .process-tile-plantLoss {
    background-color: ${plantLossColor};
    color: ${textOnPlant};
    fill: ${textOnPlant};
    stroke: ${textOnPlant};
  }
  .process-tile-uncollected {
    background-color: ${uncollectedColor};
    color: ${textOnUncollected};
    fill: ${textOnUncollected};
    stroke: ${textOnUncollected};
  }
  .process-tile-loading {
    background-color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeModalBackgroundColor : "#ffffff"};
    color: ${medGrey};
    fill: ${medGrey};
    stroke: ${medGrey};
    border: 1px solid ${medGrey};
  }

  .process-name {
    height: ${SECTION_HEIGHT_PCTS[0]}%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${props =>
      props.$getFontSize?.(props.$isLongProcessName ? 0.75 : 1)};
    font-weight: 500;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    white-space: nowrap;
    justify-content: ${props =>
      props.$isVeryLongProcessName ? "start" : "center"};
    margin-left: ${props => (props.$isVeryLongProcessName ? 6 : 0)}px;
    /* show vertical overflow (for letters with tails g,q,y,p,j) */
    /* overflow-y: visible; */
    /* problem with overflow-x and overflow-y as different values: */
    /* https://stackoverflow.com/questions/6421966/css-overflow-x-visible-and-overflow-y-hidden-causing-scrollbar-issue */
    /* instead, hack to show overflow-y: */
    padding-bottom: -${props => props.$getFontSize?.(0.5)};
    margin-bottom: -${props => props.$getFontSize?.(0.5)};
  }
  .process-state {
    min-height: ${SECTION_HEIGHT_PCTS[1] - 2}%;
    height: fit-content;
    max-height: ${props =>
      props.$hideMetricOnTile ? 100 : SECTION_HEIGHT_PCTS[1] + 1}%;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    .process-state-elapsed {
      &.negative {
        background-color: ${untaggedColor};
      }
      display: flex;
      min-height: 0;
      align-items: ${getIsIe11() ? "flex-end" : "baseline"};
      .process-state-elapsed-quantity {
        flex-grow: 3;
        min-height: 0;
        font-size: ${props => props.$getFontSize?.(2.5)};
        &.small {
          font-size: ${props => props.$getFontSize?.(1.5)};
        }
        font-weight: 600;
        line-height: 1em;
      }
      .process-state-elapsed-units {
        flex-grow: 3;
        min-height: 0;
        font-size: ${props => props.$getFontSize?.(0.9)};
      }
    }
    .process-status-or-tag {
      font-size: ${props => props.$getFontSize?.(0.75)};
      &.long-tag-name {
        font-size: ${props => props.$getFontSize?.(0.5)};
      }
      line-height: 1.2em;
      &.large-tiles {
        line-height: 1em;
      }
      max-height: ${props => (props.$hideMetricOnTile ? "100%" : "2.4em")};
      overflow: ${props => (props.$hideMetricOnTile ? "visible" : "hidden")};
      word-break: break-word;
      overflow: visible;
    }
  }
  .process-metric {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    font-size: ${props => props.$getFontSize?.(1.2)};
    opacity: 0.75;
    font-weight: 500;
    .process-metric-value {
      .process-metric-units {
        padding-left: ${props => props.$getWidth?.(0.1)};
        font-size: ${props => props.$getFontSize?.(0.6)};
      }
    }
  }
  .call-icon {
    /* not sure why this isn't responding like the others */
    height: 42%;
    width: 42%;
    margin-top: 12%;
    margin-bottom: -2%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
  .call-state-elapsed {
    display: flex;
    align-items: baseline;
  }
  .call-state-elapsed-quantity {
    font-size: ${props => props.$getFontSize?.(1.4)};
    font-weight: 500;
  }
  .call-state-elapsed-units {
    padding-left: ${props => props.$getWidth?.(0.3)};
    font-size: ${props => props.$getFontSize?.(0.8)};
  }
  /* when a tile is called, it pings out like a sonar to alert the user */
  @keyframes sonarAnimation {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.5);
      opacity: 0;
    }
  }
  .sonar {
    width: 100%;
    height: 100%;
    position: absolute;
  }
  .sonar:before,
  .sonar:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px solid ${alertColor};
    border-radius: 2px;
    animation: sonarAnimation 2s linear infinite;
    opacity: 1;
  }

  .sonar:after {
    animation-delay: 1s; /* Delay the animation for the second border */
  }
`;
